
import React, { useEffect, useMemo, useState } from "react";
import {
  ShieldCheck,
  Search,
  Save,
  Users,
  RefreshCw,
  CheckSquare,
  Square,
  ChevronDown,
  ChevronUp,
  LayoutDashboard,
  UserRound,
  GraduationCap,
  Building2,
  BriefcaseBusiness,
  BookOpen,
  ClipboardList,
  Bell,
  CreditCard,
  Wallet,
  BarChart3,
  Settings,
  Tags,
  Handshake,
  CalendarCheck,
  UserPlus,
  UserCheck,
  Clock,
  FileText,
  Megaphone,
} from "lucide-react";

const API_BASE = "/api";
const STORAGE_KEY = "certed_employee_permissions";

const ADMIN_MENUS = [
  {
    title: "Dashboard",
    icon: LayoutDashboard,
    pages: [
      { label: "Dashboard", path: "/dashboard" },
    ],
  },
  {
    title: "People",
    icon: Users,
    pages: [
      { label: "Employees", path: "/employee-list" },
      { label: "Add Employee", path: "/employee-register" },
      { label: "Trainers", path: "/trainer-list" },
      { label: "Trainers on Working", path: "/trainer-working" },
    ],
  },
  {
    title: "Partners / Recruiters",
    icon: Building2,
    pages: [
      { label: "All Partners", path: "/partner-list" },
      { label: "Partner Leads", path: "/partner-leads" },
      { label: "Recruiter Requests", path: "/admin/recruiter-requests" },
    ],
  },
  {
    title: "Marketplace",
    icon: BriefcaseBusiness,
    pages: [
      { label: "All Jobs", path: "/All-Jobs" },
      { label: "Training Opportunities", path: "/training-opportunities" },
      { label: "Job Applications", path: "/job-applications" },
      { label: "Job Matches", path: "/job-matches" },
    ],
  },
  {
    title: "Training",
    icon: BookOpen,
    pages: [
      { label: "Training Projects", path: "/training-projects" },
      { label: "Training Requests", path: "/training-requests" },
      { label: "Training Schedule", path: "/training-schedule" },
      { label: "Completed Trainings", path: "/completed-trainings" },
    ],
  },
  {
    title: "Requests",
    icon: ClipboardList,
    pages: [
      { label: "Trainer Job Requests", path: "/trainer-job-requests" },
      { label: "Partner Job Requests", path: "/partner-job-requests" },
      { label: "Trainer Applications", path: "/trainer-applications" },
      { label: "Pending Requests", path: "/pending-requests" },
      { label: "Approved Requests", path: "/approved-requests" },
      { label: "Rejected Requests", path: "/rejected-requests" },
    ],
  },
  {
    title: "Notifications",
    icon: Bell,
    pages: [
      { label: "All Notifications", path: "/job-notifications" },
      { label: "Trainer Notifications", path: "/trainer-notifications" },
      { label: "Partner Notifications", path: "/partner-notifications" },
      { label: "Job Alerts", path: "/job-alerts" },
    ],
  },
  {
    title: "Subscriptions",
    icon: CreditCard,
    pages: [
      {
        label: "All Subscriptions",
        path: "/admin/subscription-management",
      },
      {
        label: "Subscription Plans",
        path: "/admin/subscription-plans",
      },
      {
        label: "Subscription Products",
        path: "/admin/subscription-products",
      },
    ],
  },
  {
    title: "Payments",
    icon: Wallet,
    pages: [
      { label: "Trainer Payments", path: "/trainer-payments" },
      { label: "Partner Payments", path: "/partner-payments" },
      { label: "Pending Payments", path: "/pending-payments" },
      { label: "Payment History", path: "/payment-history" },
    ],
  },
  {
    title: "Reports",
    icon: BarChart3,
    pages: [
      { label: "Reports", path: "/reports" },
    ],
  },
  {
    title: "System Settings",
    icon: Settings,
    pages: [
      { label: "Users & Roles", path: "/admin/users-roles" },
      { label: "Categories & Skills", path: "/admin/categories-skills" },
      { label: "System Settings", path: "/settings" },
    ],
  },
];

const ALL_PATHS = ADMIN_MENUS.flatMap((menu) =>
  menu.pages.map((page) => page.path)
);

function readSavedPermissions() {
  try {
    const value = JSON.parse(
      localStorage.getItem(STORAGE_KEY) || "{}"
    );

    return value && typeof value === "object" ? value : {};
  } catch {
    return {};
  }
}

function normalizeEmail(email) {
  return String(email || "").trim().toLowerCase();
}

export default function UserRoles() {
  const [employees, setEmployees] = useState([]);
  const [selectedEmail, setSelectedEmail] = useState("");
  const [permissions, setPermissions] = useState({});
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [expandedMenus, setExpandedMenus] = useState({});
  const [message, setMessage] = useState("");

  useEffect(() => {
    setPermissions(readSavedPermissions());
    loadEmployees();
  }, []);

  async function loadEmployees() {
    setLoading(true);
    setMessage("");

    try {
      const response = await fetch(`${API_BASE}/employees`);

      if (!response.ok) {
        throw new Error(`Employee API failed: ${response.status}`);
      }

      const result = await response.json();

      const list = Array.isArray(result)
        ? result
        : Array.isArray(result?.data)
        ? result.data
        : Array.isArray(result?.employees)
        ? result.employees
        : [];

      setEmployees(list);

      setSelectedEmail((current) => {
        const currentExists = list.some(
          (employee) =>
            normalizeEmail(employee.email) === current
        );

        if (currentExists) return current;

        return normalizeEmail(
          list.find((employee) => employee.email)?.email
        );
      });
    } catch (error) {
      setMessage(
        `${error.message}. Please check your backend API.`
      );
    } finally {
      setLoading(false);
    }
  }

  const filteredEmployees = useMemo(() => {
    const term = search.trim().toLowerCase();

    if (!term) return employees;

    return employees.filter((employee) =>
      [
        employee.name,
        employee.fullName,
        employee.email,
        employee.phone,
      ].some((value) =>
        String(value || "").toLowerCase().includes(term)
      )
    );
  }, [employees, search]);

  const selectedEmployee = employees.find(
    (employee) =>
      normalizeEmail(employee.email) === selectedEmail
  );

  const selectedPaths = permissions[selectedEmail] || [];

  function hasPermission(path) {
    return selectedPaths.includes(path);
  }

  function togglePage(path) {
    if (!selectedEmail) return;

    setPermissions((previous) => {
      const current = previous[selectedEmail] || [];

      const next = current.includes(path)
        ? current.filter((item) => item !== path)
        : [...current, path];

      return {
        ...previous,
        [selectedEmail]: next,
      };
    });

    setMessage("");
  }

  function toggleMenu(menu) {
    const paths = menu.pages.map((page) => page.path);
    const allSelected = paths.every(hasPermission);

    setPermissions((previous) => {
      const current = previous[selectedEmail] || [];

      const next = allSelected
        ? current.filter((path) => !paths.includes(path))
        : [...new Set([...current, ...paths])];

      return {
        ...previous,
        [selectedEmail]: next,
      };
    });

    setMessage("");
  }

  function enableAll() {
    if (!selectedEmail) return;

    setPermissions((previous) => ({
      ...previous,
      [selectedEmail]: [...ALL_PATHS],
    }));

    setMessage("");
  }

  function disableAll() {
    if (!selectedEmail) return;

    setPermissions((previous) => ({
      ...previous,
      [selectedEmail]: [],
    }));

    setMessage("");
  }

  function toggleExpanded(title) {
    setExpandedMenus((previous) => ({
      ...previous,
      [title]: !previous[title],
    }));
  }

  function savePermissions() {
    if (!selectedEmail) {
      setMessage("Please select an employee first.");
      return;
    }

    setSaving(true);

    try {
      const updated = {
        ...permissions,
        [selectedEmail]: [...new Set(selectedPaths)],
      };

      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify(updated)
      );

      setPermissions(updated);

      window.dispatchEvent(
        new Event("employee-permissions-updated")
      );

      setMessage(
        `Menu permissions saved for ${
          selectedEmployee?.name ||
          selectedEmployee?.fullName ||
          selectedEmail
        }.`
      );
    } catch (error) {
      setMessage("Unable to save permissions.");
    } finally {
      setSaving(false);
    }
  }

  const totalEnabled = selectedPaths.length;

  return (
    <div className="user-roles-page">
      <style>{`
        .user-roles-page {
          padding: 24px;
          color: #302641;
          font-family: inherit;
        }

        .ur-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 16px;
          flex-wrap: wrap;
          margin-bottom: 22px;
        }

        .ur-title-wrap {
          display: flex;
          align-items: center;
          gap: 13px;
        }

        .ur-title-icon {
          display: grid;
          place-items: center;
          width: 49px;
          height: 49px;
          border-radius: 12px;
          color: #3E0F8D;
          background: #f1eafb;
        }

        .ur-header h2 {
          margin: 0 0 5px;
          font-size: 23px;
          color: #3E0F8D;
        }

        .ur-header p {
          margin: 0;
          font-size: 13px;
          color: #81778d;
        }

        .ur-layout {
          display: grid;
          grid-template-columns: 280px minmax(0, 1fr);
          gap: 20px;
          align-items: start;
        }

        .ur-panel {
          min-width: 0;
          background: #fff;
          border: 1px solid #e9e2f1;
          border-radius: 14px;
          overflow: hidden;
        }

        .ur-panel-heading {
          padding: 16px;
          font-weight: 800;
          color: #3E0F8D;
          border-bottom: 1px solid #eee8f4;
        }

        .ur-search {
          display: flex;
          align-items: center;
          gap: 8px;
          margin: 13px;
          padding: 10px;
          border: 1px solid #ded5e9;
          border-radius: 9px;
        }

        .ur-search input {
          min-width: 0;
          width: 100%;
          border: 0;
          outline: none;
          font-size: 13px;
        }

        .ur-employees {
          max-height: 600px;
          overflow-y: auto;
          padding: 0 10px 10px;
        }

        .ur-employee {
          display: block;
          width: 100%;
          padding: 12px;
          margin-bottom: 5px;
          border: 1px solid transparent;
          border-radius: 9px;
          text-align: left;
          background: #fff;
          cursor: pointer;
        }

        .ur-employee.selected {
          border-color: #d8c4f0;
          background: #f4effb;
        }

        .ur-employee strong,
        .ur-employee small {
          display: block;
          overflow-wrap: anywhere;
        }

        .ur-employee strong {
          font-size: 13px;
          color: #3c2c50;
        }

        .ur-employee small {
          margin-top: 5px;
          font-size: 11px;
          color: #81778d;
        }

        .ur-content {
          padding: 18px;
        }

        .ur-selected {
          display: flex;
          align-items: center;
          justify-content: space-between;
          flex-wrap: wrap;
          gap: 12px;
          margin-bottom: 18px;
        }

        .ur-selected h3 {
          margin: 0 0 5px;
          color: #3E0F8D;
          font-size: 18px;
        }

        .ur-selected p {
          margin: 0;
          color: #81778d;
          font-size: 12px;
          overflow-wrap: anywhere;
        }

        .ur-count {
          padding: 9px 12px;
          border-radius: 8px;
          background: #f2ecfa;
          color: #3E0F8D;
          font-weight: 800;
          font-size: 12px;
        }

        .ur-actions {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
          margin-bottom: 18px;
        }

        .ur-btn {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 7px;
          padding: 10px 13px;
          border: 1px solid #ded5e9;
          border-radius: 8px;
          background: #fff;
          color: #4c3a60;
          cursor: pointer;
          font-size: 12px;
          font-weight: 700;
        }

        .ur-btn.primary {
          background: #3E0F8D;
          border-color: #3E0F8D;
          color: #fff;
        }

        .ur-btn:disabled {
          opacity: .55;
          cursor: not-allowed;
        }

        .ur-menu {
          border: 1px solid #e9e2f1;
          border-radius: 10px;
          margin-bottom: 11px;
          overflow: hidden;
        }

        .ur-menu-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 10px;
          padding: 12px 14px;
          background: #faf8fd;
          border-bottom: 1px solid #eee8f4;
        }

        .ur-menu-title {
          display: flex;
          align-items: center;
          gap: 9px;
          color: #3E0F8D;
          font-size: 13px;
          font-weight: 800;
        }

        .ur-menu-actions {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .ur-menu-actions button {
          padding: 3px;
          border: 0;
          background: transparent;
          color: #6f42a6;
          cursor: pointer;
          font-size: 11px;
          font-weight: 800;
        }

        .ur-page {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 12px 14px;
          border-bottom: 1px solid #f1edf5;
          cursor: pointer;
          font-size: 13px;
        }

        .ur-page:last-child {
          border-bottom: 0;
        }

        .ur-page input {
          width: 16px;
          height: 16px;
          accent-color: #3E0F8D;
          cursor: pointer;
        }

        .ur-path {
          margin-left: auto;
          color: #91879c;
          font-size: 11px;
          overflow-wrap: anywhere;
          text-align: right;
        }

        .ur-message {
          padding: 12px 14px;
          margin-bottom: 16px;
          border-radius: 8px;
          background: #f2ecfa;
          color: #3E0F8D;
          font-size: 13px;
        }

        .ur-empty {
          padding: 25px 15px;
          text-align: center;
          color: #81778d;
          font-size: 13px;
        }

        .ur-note {
          padding: 13px;
          margin-top: 15px;
          border: 1px solid #eadfb5;
          border-radius: 9px;
          background: #fffdf3;
          color: #655625;
          font-size: 12px;
          line-height: 1.6;
        }

        @media (max-width: 900px) {
          .ur-layout {
            grid-template-columns: 1fr;
          }

          .ur-employees {
            max-height: 300px;
          }

          .user-roles-page {
            padding: 14px;
          }
        }
      `}</style>

      <div className="ur-header">
        <div className="ur-title-wrap">
          <div className="ur-title-icon">
            <ShieldCheck size={26} />
          </div>

          <div>
            <h2>Users & Roles</h2>
            <p>
              Assign admin menu permissions to individual employees.
            </p>
          </div>
        </div>

        <button className="ur-btn" onClick={loadEmployees}>
          <RefreshCw size={15} />
          Refresh Employees
        </button>
      </div>

      {message && <div className="ur-message">{message}</div>}

      <div className="ur-layout">
        <section className="ur-panel">
          <div className="ur-panel-heading">
            <Users
              size={17}
              style={{
                verticalAlign: "middle",
                marginRight: 8,
              }}
            />
            Employees ({employees.length})
          </div>

          <div className="ur-search">
            <Search size={16} />
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search employee..."
            />
          </div>

          {loading ? (
            <div className="ur-empty">Loading employees...</div>
          ) : filteredEmployees.length === 0 ? (
            <div className="ur-empty">
              No employees found.
            </div>
          ) : (
            <div className="ur-employees">
              {filteredEmployees.map((employee, index) => {
                const email = normalizeEmail(employee.email);

                return (
                  <button
                    type="button"
                    key={email || employee.id || index}
                    className={`ur-employee ${
                      selectedEmail === email ? "selected" : ""
                    }`}
                    onClick={() => {
                      setSelectedEmail(email);
                      setMessage("");
                    }}
                  >
                    <strong>
                      {employee.name ||
                        employee.fullName ||
                        "Employee"}
                    </strong>
                    <small>{email || "Email missing"}</small>
                  </button>
                );
              })}
            </div>
          )}
        </section>

        <section className="ur-panel">
          <div className="ur-panel-heading">
            Admin Menu Access Control
          </div>

          {!selectedEmployee ? (
            <div className="ur-empty">
              Select an employee with a valid email address.
            </div>
          ) : (
            <div className="ur-content">
              <div className="ur-selected">
                <div>
                  <h3>
                    {selectedEmployee.name ||
                      selectedEmployee.fullName ||
                      "Employee"}
                  </h3>
                  <p>{selectedEmail}</p>
                </div>

                <div className="ur-count">
                  {totalEnabled} / {ALL_PATHS.length} pages enabled
                </div>
              </div>

              <div className="ur-actions">
                <button
                  type="button"
                  className="ur-btn"
                  onClick={enableAll}
                >
                  <CheckSquare size={15} />
                  Give All Access
                </button>

                <button
                  type="button"
                  className="ur-btn"
                  onClick={disableAll}
                >
                  <Square size={15} />
                  Remove All Access
                </button>

                <button
                  type="button"
                  className="ur-btn primary"
                  onClick={savePermissions}
                  disabled={saving}
                >
                  <Save size={15} />
                  {saving ? "Saving..." : "Save Permissions"}
                </button>
              </div>

              {ADMIN_MENUS.map((menu) => {
                const Icon = menu.icon;
                const enabled = menu.pages.filter((page) =>
                  hasPermission(page.path)
                ).length;

                const allEnabled = enabled === menu.pages.length;
                const expanded =
                  expandedMenus[menu.title] !== false;

                return (
                  <div className="ur-menu" key={menu.title}>
                    <div className="ur-menu-header">
                      <div className="ur-menu-title">
                        <Icon size={17} />
                        <span>{menu.title}</span>
                        <span>
                          ({enabled}/{menu.pages.length})
                        </span>
                      </div>

                      <div className="ur-menu-actions">
                        <button
                          type="button"
                          onClick={() => toggleMenu(menu)}
                        >
                          {allEnabled
                            ? "Disable All"
                            : "Enable All"}
                        </button>

                        <button
                          type="button"
                          aria-label={
                            expanded ? "Collapse menu" : "Expand menu"
                          }
                          onClick={() => toggleExpanded(menu.title)}
                        >
                          {expanded ? (
                            <ChevronUp size={17} />
                          ) : (
                            <ChevronDown size={17} />
                          )}
                        </button>
                      </div>
                    </div>

                    {expanded &&
                      menu.pages.map((page) => (
                        <label
                          className="ur-page"
                          key={page.path}
                        >
                          <input
                            type="checkbox"
                            checked={hasPermission(page.path)}
                            onChange={() => togglePage(page.path)}
                          />

                          <span>{page.label}</span>
                          <span className="ur-path">
                            {page.path}
                          </span>
                        </label>
                      ))}
                  </div>
                );
              })}

              <div className="ur-note">
                <strong>Access control note:</strong> These permissions
                are currently stored in browser localStorage for
                development/testing. To make them apply securely across
                devices and sessions, save them in the backend database
                and validate every protected route and API request.
                System Settings and Users & Roles should normally remain
                restricted to administrators.
              </div>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
